import { Cat, KINDS } from '../../../gameplay/registry';
import { C } from '../../../gameplay/types';
import { FX_ONE } from '../fixed';
import { MAX_ENTITIES, Order, StanceId, UState, type World } from '../world';
import { setDestination } from './movement';

/** Monks heal every friendly unit whose centre is within one tile of theirs. */
export const HEAL_RADIUS = FX_ONE;
/** An idle monk looks for wounded friends this far away (tiles) and walks over to them. */
const SEEK_TILES = 8;

/** 1 for kinds that heal, by kind index (built once). */
let healerKinds: Uint8Array | null = null;
const isHealer = (w: World, s: number) => (healerKinds ??= Uint8Array.from(KINDS, (k) => ((k.unit?.classes ?? 0) & C.Healer ? 1 : 0)))[w.kind[s]];
/** Units healed this pass carry the pass's stamp (no stacking from several monks, no allocation). */
const healedStamp = new Int32Array(MAX_ENTITIES);
let stamp = 0;

/**
 * Healing. A wounded unit near a monk regains hit points at the rate it was trained: from nothing
 * to full in its training time. Several monks around the same unit do not stack. Monks do not heal
 * themselves, and nothing heals buildings (villagers repair those).
 */
export function updateHealing(w: World): void {
  const n = w.highSlot;
  // Units healed this tick (no stacking from several monks).
  stamp = (stamp + 1) | 0 || 1;
  for (let m = 0; m < n; m++) {
    if (!w.alive[m] || w.cat[m] !== Cat.Unit || !isHealer(w, m)) continue;
    const owner = w.owner[m];
    let busy = false;
    w.forUnitsInRadius(w.x[m], w.y[m], HEAL_RADIUS, (t) => {
      if (t === m || !w.alive[t] || healedStamp[t] === stamp || w.hp[t] >= w.maxHp[t]) return;
      if (w.owner[t] !== owner && w.areEnemies(owner, w.owner[t])) return;
      if (w.owner[t] < 0) return;
      healedStamp[t] = stamp;
      busy = true;
      const ticks = Math.max(1, w.players[w.owner[t]].stats[w.kind[t]].trainTicks);
      // maxHp over trainTicks per tick, carried in 1/1024 hit points so slow rates add up exactly.
      const acc = w.healAcc[t] + Math.trunc((w.maxHp[t] * 1024) / ticks);
      const whole = acc >> 10;
      w.healAcc[t] = acc & 1023;
      w.hp[t] = Math.min(w.maxHp[t], w.hp[t] + whole);
      if (w.hp[t] >= w.maxHp[t]) w.healAcc[t] = 0;
      if ((w.tick + t) % 20 === 0) w.events.push({ e: 'heal', x: w.x[t], y: w.y[t] });
    });
    if (busy) {
      if (!w.moving[m]) w.state[m] = UState.Healing;
      continue;
    }
    if (w.state[m] === UState.Healing && !w.moving[m]) w.state[m] = UState.Idle;
    // An idle monk walks to the most wounded friendly unit nearby.
    if (w.order[m] !== Order.None || w.stance[m] === StanceId.Hold || (w.tick + m) % 20 !== 0) continue;
    let best = -1;
    let bestScore = 0;
    w.forUnitsInRadius(w.x[m], w.y[m], SEEK_TILES * FX_ONE, (t, d2) => {
      if (t === m || !w.alive[t] || w.hp[t] >= w.maxHp[t] || w.owner[t] < 0 || w.areEnemies(owner, w.owner[t])) return;
      if (w.naval[t] !== w.naval[m]) return;
      // Most hit points missing first, nearer first among equals.
      const score = (w.maxHp[t] - w.hp[t]) * 1_000_000 - Math.trunc(d2 / FX_ONE);
      if (best < 0 || score > bestScore) {
        best = t;
        bestScore = score;
      }
    });
    if (best >= 0) {
      w.state[m] = UState.Walking;
      setDestination(w, m, w.x[best], w.y[best]);
    }
  }
}
